import {
  AuthContext,
  CommonContext,
  camelize,
  useApi,
} from '@repo-level/core-front';
import type { ICMSLottery } from 'api/cms/lottery';
import { cmsLotteries } from 'api/cms/lottery';
import type { Lottery, LotteryStatus } from 'api/lotteries';
import {
  lotteries as fetchLotteries,
  lottery as fetchLottery,
  lotteryStatus,
  lotteryStatuses,
} from 'api/lotteries';
import { ChannelType } from 'contexts/centrifugo/types';
import { useSubscription } from 'contexts/centrifugo/useSubscription';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeStatus } from './helpers';
import type { State } from './reducer';
import { ACTION_TYPE, reducer } from './reducer';
import { useLotteryOptOuts } from './useLotteryOptOuts';
import { useLotteryWins } from './useLotteryWins';

interface ILotteriesContext extends State {
  /** sorted by `show_from` */
  cmsLotteries: ICMSLottery[];
  cmsLotteriesPending: boolean;
  refreshLottery: (id: number) => Promise<void>;
  fetchLotteryStatus: (id: number) => Promise<void>;
  markWinSeen: (lotteryId: number) => void;
  dropWin: (lotteryId: number) => void;
  /** lotteries the player turned off in the deposit form */
  optOutIds: number[];
  setLotteryOptOut: (lotteryId: number, isOptOut: boolean) => void;
}

export const LotteriesContext = createContext<ILotteriesContext>(null as any);

const initialState: State = {
  lotteries: [],
  lotteriesPending: true,
  statuses: [],
  winQueue: [],
  seenWinIds: [],
  wonPrizeIds: [],
  wonPrizesPending: true,
};

export const LotteriesContextProvider: FC = ({ children }) => {
  const [lotteriesState, dispatchLotteries] = useReducer(reducer, initialState);

  const { user } = useContext(AuthContext);
  const { project } = useContext(CommonContext);
  const {
    i18n: { language },
  } = useTranslation();

  // pending from the start, the page must not take the empty list for none;
  // a request cancelled by a language change would end the pending of the next one
  const [requestCmsLotteries, { data: cmsData, pending: cmsPending }] = useApi(
    cmsLotteries,
    { pending: true },
    { dispatchOnCancel: false }
  );

  useEffect(() => {
    requestCmsLotteries(language);
  }, [language, requestCmsLotteries]);

  const userId = user?.id;
  const isProjectLoaded = !!project;
  const isServerStorageEnabled = project?.userStorageItems ?? false;

  useEffect(() => {
    (async () => {
      const lotteries = await fetchLotteries()
        .then(({ data }) => data)
        .catch(() => [] as Lottery[]);

      dispatchLotteries({
        type: ACTION_TYPE.SET_LOTTERIES,
        payload: { lotteries },
      });
    })();
  }, []);

  useEffect(() => {
    dispatchLotteries({ type: ACTION_TYPE.RESET_PLAYER });

    if (!userId) return;

    let isCancelled = false;

    (async () => {
      try {
        const { data: statuses } = await lotteryStatuses();

        if (isCancelled) return;

        dispatchLotteries({
          type: ACTION_TYPE.SET_STATUSES,
          payload: { statuses: statuses.map(normalizeStatus) },
        });
      } catch {}
    })();

    return () => {
      isCancelled = true;
    };
  }, [userId]);

  const { checkWins, markWinSeen, dropWin } = useLotteryWins({
    dispatch: dispatchLotteries,
    userId,
    isProjectLoaded,
    isServerStorageEnabled,
  });

  const { optOutIds, setLotteryOptOut } = useLotteryOptOuts({
    userId,
    isProjectLoaded,
    isServerStorageEnabled,
  });

  const refreshLottery = useCallback(async (id: number) => {
    try {
      const { data } = await fetchLottery(id);

      // a 404 resolves, with no lottery: one SS doesn't have
      if (!data?.id) return;

      dispatchLotteries({
        type: ACTION_TYPE.UPDATE_LOTTERIES,
        payload: { lotteries: [data] },
      });
    } catch {}
  }, []);

  useSubscription<Lottery[] | Lottery>(
    'lotteries_changes',
    (data) => {
      const changed = camelize(data) as Lottery[] | Lottery;
      const lotteries = Array.isArray(changed) ? changed : [changed];

      dispatchLotteries({
        type: ACTION_TYPE.UPDATE_LOTTERIES,
        payload: { lotteries },
      });

      // a message without the tickets count would leave a stale one
      for (const { id, lotteryTicketsCount } of lotteries) {
        if (lotteryTicketsCount === undefined) refreshLottery(id);
      }

      // the prizes with no ticket come with no status message
      if (lotteries.some(({ finishedAt }) => finishedAt)) checkWins();
    },
    ChannelType.Public
  );

  useSubscription<LotteryStatus>('lotteries_statuses', (data) => {
    dispatchLotteries({
      type: ACTION_TYPE.UPDATE_STATUS,
      payload: {
        status: normalizeStatus(camelize(data) as LotteryStatus),
        isLive: true,
      },
    });
  });

  const fetchLotteryStatus = useCallback(
    async (id: number) => {
      if (!userId) return;

      try {
        const { data } = await lotteryStatus(id);

        dispatchLotteries({
          type: ACTION_TYPE.UPDATE_STATUS,
          payload: { status: normalizeStatus(data) },
        });
      } catch {}
    },
    [userId]
  );

  const contextValue = useMemo(
    () => ({
      ...lotteriesState,
      cmsLotteries: cmsData ?? [],
      cmsLotteriesPending: cmsPending,
      refreshLottery,
      fetchLotteryStatus,
      markWinSeen,
      dropWin,
      optOutIds,
      setLotteryOptOut,
    }),
    [
      optOutIds,
      setLotteryOptOut,
      lotteriesState,
      cmsData,
      cmsPending,
      refreshLottery,
      fetchLotteryStatus,
      markWinSeen,
      dropWin,
    ]
  );

  return (
    <LotteriesContext.Provider value={contextValue}>
      {children}
    </LotteriesContext.Provider>
  );
};
